import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, BadgePercent } from 'lucide-react';
import { NAV_CATEGORIES, CATEGORIES_ORDER } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { getJourneyId, trackEvent, trackPageLifecycle } from '../lib/customerEventTracker';
import { appendLeadToGoogleSheets, isGoogleSheetsConfigured } from '../lib/googleSheetsLead';
import { goToPostSubmit } from '../lib/postSubmitGuest';
import FormLegalNotice from '../components/FormLegalNotice';
import TrustStrip from '../components/TrustStrip';

const CONTACT_SERVICE_ORDER = [...CATEGORIES_ORDER, 'OTHER'];

const orderContactServiceSelection = (picked) => CONTACT_SERVICE_ORDER.filter((c) => picked.includes(c));

const Contact = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState({ name: '', phone: '', services: [], message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'CONTACT_SUPPORT_FORM',
      form_name: 'ContactForm',
      open_event_name: 'contact_form_open',
    }),
    [],
  );

  const contactInfo = [
    { id: 'office', icon: MapPin, titleKey: 'contact.info_office', titleFb: 'Office', text: "1st Floor, MIG 88, Rd Number 1, above Dr Kiran's Eye Hospital, Kukatpally Housing Board Colony, K P H B Phase 1, Kukatpally, Hyderabad, Telangana - 500072" },
    { id: 'email', icon: Mail, titleKey: 'contact.info_email', titleFb: 'Email', text: 'contact@solvetax.in', href: 'mailto:contact@solvetax.in' },
    { id: 'support', icon: Mail, titleKey: 'contact.info_support', titleFb: 'Support', text: 'support@solvetax.in', href: 'mailto:support@solvetax.in' },
    { id: 'phone', icon: Phone, titleKey: 'contact.info_phone', titleFb: 'Phone', text: '+91 93460 04430', href: 'tel:+919346004430' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data.name || !/^[0-9]{10}$/.test(data.phone)) {
      trackEvent({
        event_name: 'contact_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ContactForm',
        funnel_name: 'CONTACT_SUPPORT_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
        error_code: 'validation_error',
        error_message: 'Name or phone validation failed',
      });
      toast.error(t('contact.toast_name_phone', 'Please enter your name and a valid 10-digit phone number'));
      return;
    }
    const labelsOrdered = orderContactServiceSelection(data.services).map((code) => {
      if (code === 'OTHER') return t('contact.other_services', 'Other Services');
      const cat = NAV_CATEGORIES.find((c) => c.category === code);
      return cat ? t(`category.${cat.category}`, cat.label) : code;
    });
    const payload = {
      your_name: data.name.trim(),
      phone_number: data.phone.trim(),
      service_required: labelsOrdered.length ? labelsOrdered.join(', ') : null,
      services_required: orderContactServiceSelection(data.services),
      your_message: data.message.trim() || null,
    };

    setLoading(true);
    trackEvent({
      event_name: 'contact_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ContactForm',
      funnel_name: 'CONTACT_SUPPORT_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
      phone_number: payload.phone_number,
      service_code: payload.services_required.length ? payload.services_required.join(',') : null,
    });
    try {
      if (!isGoogleSheetsConfigured()) {
        trackEvent({
          event_name: 'contact_submit_failed',
          event_action: 'submit',
          event_status: 'failed',
          event_category: 'form',
          form_name: 'ContactForm',
          funnel_name: 'CONTACT_SUPPORT_FORM',
          funnel_step_number: 2,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
          phone_number: payload.phone_number,
          service_code: payload.services_required.length ? payload.services_required.join(',') : null,
          error_code: 'missing_sheets_env',
          error_message: 'REACT_APP_GOOGLE_SHEETS_WEBAPP_URL not set',
        });
        toast.error(
          t(
            'contact.err_sheets_missing',
            'Google Sheets URL is missing. Add REACT_APP_GOOGLE_SHEETS_WEBAPP_URL to frontend/.env and restart.',
          ),
        );
        return;
      }
      appendLeadToGoogleSheets({
        form: 'contact',
        summary: `${payload.your_name} | ${payload.phone_number} | ${payload.service_required || '—'}`,
        detail: payload,
      });
      trackEvent({
        event_name: 'contact_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'ContactForm',
        funnel_name: 'CONTACT_SUPPORT_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONTACT_SUPPORT_FORM', { reset: true }),
        phone_number: payload.phone_number,
        service_code: payload.services_required.length ? payload.services_required.join(',') : null,
        api_name: 'google_sheets',
        api_status_code: null,
      });
      toast.success(t('contact.toast_success', 'Our support team will contact you within 2 hours.'));
      setSuccess({ your_name: payload.your_name, phone_number: payload.phone_number });
      setData({ name: '', phone: '', services: [], message: '' });
    } catch (err) {
      const msg = err?.message || 'Unexpected error';
      trackEvent({
        event_name: 'contact_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ContactForm',
        funnel_name: 'CONTACT_SUPPORT_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
        phone_number: payload.phone_number,
        service_code: payload.services_required.length ? payload.services_required.join(',') : null,
        error_code: 'unexpected',
        error_message: msg,
      });
      toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg }));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return <SuccessView payload={success} onAbout={() => goToPostSubmit(navigate, success.your_name)} />;
  }

  return (
    <div className="pt-28">
      <section className="py-14 hero-gradient">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h1 className="ui-title-xl mb-3">
            {t('contact.hero_title', 'Contact')} <span className="text-[#14A098]">{t('contact.hero_accent', 'Us')}</span>
          </h1>
          <p className="ui-body mb-6">{t('contact.hero_sub', 'Get in touch with our expert team for all your tax and financial needs')}</p>
          <TrustStrip />
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {contactInfo.map((c) => (
              <Card key={c.id} className="p-5 rounded-2xl border border-gray-100 flex gap-4 items-start card-lift">
                <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0"><c.icon className="w-5 h-5 text-[#14A098]" /></div>
                <div>
                  <div className="font-semibold text-[#0f2540]">{t(c.titleKey, c.titleFb)}</div>
                  {c.href ? <a href={c.href} className="text-sm text-gray-600 hover:text-[#14A098]">{c.text}</a> : <div className="text-sm text-gray-600">{c.text}</div>}
                </div>
              </Card>
            ))}
            <Card className="p-5 rounded-2xl bg-gradient-to-br from-[#0f2540] to-[#14A098] text-white">
              <div className="flex items-center gap-2 mb-2"><BadgePercent className="w-5 h-5" /><span className="font-semibold">{t('offer.title', 'Special Offer')}</span></div>
              <p className="text-sm text-teal-100">
                {t('offer.body_pre', 'Submit the form and get a flat')}{' '}
                <b className="text-white">{t('offer.discount', '10% discount')}</b>{' '}
                {t('offer.body_after', 'on your first service with SolveTax.')}
              </p>
            </Card>
          </div>

          <Card className="lg:col-span-3 p-6 md:p-8 rounded-2xl border border-gray-100 shadow-lg bg-white">
            <h2 className="text-2xl font-bold text-[#0f2540] mb-1">{t('contact.form_title', 'Contact Support')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('contact.form_sub', 'Fill out the form and our support team will contact you within 2 hours.')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name" className="text-sm mb-2 block">{t('contact.label_name', 'Your Name')} <span className="text-rose-500">*</span></Label>
                  <Input id="contact-name" name="name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder={t('contact.ph_name', 'Full name')} autoComplete="name" />
                </div>
                <div>
                  <Label htmlFor="contact-phone" className="text-sm mb-2 block">{t('contact.label_phone', 'Phone Number')} <span className="text-rose-500">*</span></Label>
                  <Input id="contact-phone" name="phone" inputMode="numeric" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder={t('contact.ph_phone', '10-digit mobile')} autoComplete="tel" />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-2 block" id="contact-services-label">{t('contact.label_service', 'Service Required')}</Label>
                <p className="text-xs text-gray-500 mb-2">{t('contact.service_multi_hint', 'Select all categories you need help with (optional).')}</p>
                <div
                  role="group"
                  aria-labelledby="contact-services-label"
                  className="rounded-md border border-input bg-background p-3 space-y-2.5 max-h-52 overflow-y-auto"
                >
                  {NAV_CATEGORIES.map((cat) => {
                    const checked = data.services.includes(cat.category);
                    return (
                      <label
                        key={cat.category}
                        htmlFor={`contact-svc-${cat.category}`}
                        className="flex items-start gap-2.5 cursor-pointer text-sm font-medium text-[#0f2540] leading-snug"
                      >
                        <Checkbox
                          id={`contact-svc-${cat.category}`}
                          className="mt-0.5"
                          checked={checked}
                          onCheckedChange={(c) => {
                            const on = c === true;
                            setData((prev) => ({
                              ...prev,
                              services: on
                                ? orderContactServiceSelection([...new Set([...prev.services, cat.category])])
                                : prev.services.filter((s) => s !== cat.category),
                            }));
                          }}
                        />
                        <span>{t(`category.${cat.category}`, cat.label)}</span>
                      </label>
                    );
                  })}
                  <label
                    htmlFor="contact-svc-OTHER"
                    className="flex items-start gap-2.5 cursor-pointer text-sm font-medium text-[#0f2540] leading-snug"
                  >
                    <Checkbox
                      id="contact-svc-OTHER"
                      className="mt-0.5"
                      checked={data.services.includes('OTHER')}
                      onCheckedChange={(c) => {
                        const on = c === true;
                        setData((prev) => ({
                          ...prev,
                          services: on
                            ? orderContactServiceSelection([...new Set([...prev.services, 'OTHER'])])
                            : prev.services.filter((s) => s !== 'OTHER'),
                        }));
                      }}
                    />
                    <span>{t('contact.other_services', 'Other Services')}</span>
                  </label>
                </div>
              </div>
              <div>
                <Label htmlFor="contact-message" className="text-sm mb-2 block">{t('contact.label_message', 'Your Message')}</Label>
                <Textarea id="contact-message" name="message" rows={5} value={data.message} onChange={(e) => setData({ ...data, message: e.target.value })} placeholder={t('contact.ph_message', 'Tell us about your requirement')} />
              </div>
              <div className="flex flex-wrap gap-3 pt-2 items-center">
                <Button type="submit" disabled={loading} size="lg" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 shadow-md min-w-[200px]">
                  {loading ? t('contact.btn_sending', 'Sending...') : (<>{t('form.submit_details', 'Submit Details')} <Send className="w-4 h-4 ml-2" /></>)}
                </Button>
                <Button type="button" variant="outline" onClick={() => setData({ name: '', phone: '', services: [], message: '' })} className="rounded-full h-12 border-gray-300">
                  {t('contact.btn_reset', 'Reset')}
                </Button>
                <FormLegalNotice />
              </div>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
};

const SuccessView = ({ payload, onAbout }) => {
  const { t } = useLanguage();
  useEffect(() => {
    const timer = setTimeout(() => onAbout(), 3000);
    return () => clearTimeout(timer);
  }, [onAbout]);

  const firstName = payload.your_name.split(' ')[0];

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Card className="p-8 md:p-12 rounded-2xl border border-gray-100 shadow-xl bg-white text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-3">{t('contact.success_title', 'Thank you, {name}!', { name: firstName })}</h1>
          <p className="text-gray-600 mb-6">{t('contact.success_body', 'Our support team will reach out at +91 {phone} within 2 hours.', { phone: payload.phone_number })}</p>
          <p className="text-sm text-gray-500">{t('contact.success_redirect', 'Redirecting...')}</p>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
